/**
 * FieldError — shared inline form-error announcement helper.
 *
 * Phase L.2 P1 batch fix for the audit's Section 5.3 pattern:
 * every form on the audit surface shows inline validation errors
 * as adjacent `<p>` elements WITHOUT any of the semantic wiring
 * assistive tech needs (`aria-describedby` from the input,
 * `aria-invalid={hasError}`, `role="alert"` / `aria-live` on the
 * error message).
 *
 * Contract:
 *   - `<FieldError id="<field-id>-error" message="…" />` renders a
 *     visible `<p role="alert" aria-live="polite">` when `message`
 *     is truthy.
 *   - When `message` is null / undefined / empty string, the
 *     component renders NOTHING (React `null`). The parent input's
 *     `aria-describedby` should also be gated on the same truthy
 *     check so it doesn't dangle when the error clears.
 *
 * Consumer wiring pattern (recommended):
 *   ```
 *   const errorId = "contact-email-error";
 *   const showError = form.email && !emailOk;
 *   <Input
 *     id="contact-email"
 *     aria-invalid={showError || undefined}
 *     aria-describedby={showError ? errorId : undefined}
 *     ...
 *   />
 *   <FieldError id={errorId} message={showError ? "Please enter…" : null} />
 *   ```
 *
 * Style:
 *   - Matches the existing brand pattern used by Contact.jsx +
 *     BusinessIntakePage.jsx (`text-xs text-red-600 mt-1`).
 *   - `className` prop lets callers override / extend for site-
 *     specific density (e.g. `mb-3` on Map's waitlist form).
 *
 * Family L discipline: NO inline hex, NO `prefers-color-scheme`.
 * The `text-red-600` Tailwind utility is the shared error color
 * across the app; centralising it here keeps drift bounded.
 */
import React from "react";

export default function FieldError({
  id,
  message,
  className = "",
  testId,
}) {
  if (!message) return null;
  return (
    <p
      id={id}
      role="alert"
      aria-live="polite"
      className={`mt-1 text-xs text-red-600 ${className}`.trim()}
      data-testid={testId || id}
    >
      {message}
    </p>
  );
}
